import { useEffect, useRef, useState, type CSSProperties } from "react";
import { toast } from "./components/Kit";
import { ShellIcon } from "./components/ShellIcons";
import { api } from "./api/client";
import type { WaLabel } from "./labels";

// 负责的同事 (api console/owner.go): JH or MAD, picked here — WhatsApp keeps a
// phone's labels to the phone, so the label lives on sales.lazior.com and is
// shown wherever the customer is listed. One copy for the whole console: a
// pick in the chat shows at once in the list and on 跟进.

interface OwnersState {
  names: string[];
  owners: Record<string, string>;
}

let state: OwnersState = { names: [], owners: {} };
let loaded: Promise<void> | null = null;
const subs = new Set<(s: OwnersState) => void>();

function publish(next: OwnersState) {
  state = next;
  for (const f of subs) f(state);
}

export function reloadOwners(): Promise<void> {
  loaded = api.owners().then(
    (r) => publish({ names: r.names ?? [], owners: r.owners ?? {} }),
    () => undefined,
  );
  return loaded;
}

/** Who can be picked and who has whom, kept fresh across the console. */
export function useOwners(): OwnersState {
  const [s, setS] = useState(state);
  useEffect(() => {
    subs.add(setS);
    if (!loaded) void reloadOwners();
    return () => {
      subs.delete(setS);
    };
  }, []);
  return s;
}

/** Give customers to a colleague ("" = nobody); the console sees it at once. */
export async function setOwners(waIDs: string[], owner: string): Promise<void> {
  await api.setOwners(waIDs, owner);
  const owners = { ...state.owners };
  for (const w of waIDs) {
    if (owner) owners[w] = owner;
    else delete owners[w];
  }
  publish({ ...state, owners });
}

const OWNER_RGB: Record<string, string> = { JH: "106,108,108", MAD: "175,233,102" };

/** A colleague as the phone labels them: JH grey, MAD green. */
export function ownerLabel(name: string): WaLabel {
  return { key: `owner:${name}`, text: name, rgb: OWNER_RGB[name.toUpperCase()] ?? "83,189,235" };
}

/**
 * The 负责 chip: the colleague's label, or 选负责; a tap picks another. Its menu is the console's glass (prototype
 * chat.css .chat-pick-m), opening upward when the chip sits near the bottom; a pick is said at the top (`who`: the
 * customer, as the prototype words it) and the chip pops in its new colour.
 */
export function OwnerPick({ waID, compact, who }: { waID: string; compact?: boolean; who?: string }) {
  const { names, owners } = useOwners();
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const [busy, setBusy] = useState(false);
  // Each pick draws the chip again, so its pop plays (labels.css own-pop) — not every time a chat opens.
  const [picked, setPicked] = useState(0);
  const box = useRef<HTMLSpanElement | null>(null);
  const cur = owners[waID] ?? "";
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  async function pick(name: string) {
    setOpen(false);
    if (name === cur) return;
    setBusy(true);
    const whose = who ? `${who} ` : "";
    try {
      await setOwners([waID], name);
      setPicked((n) => n + 1);
      toast(name ? `${whose}交给 ${name}` : `${whose}没有人负责了`);
    } catch {
      toast("没交出去，再选一次", "bad");
    } finally {
      setBusy(false);
    }
  }
  const l = cur ? ownerLabel(cur) : null;
  return (
    <span className={`own-pick${compact ? " compact" : ""}`} ref={box}>
      <button
        key={picked}
        type="button"
        className={`own-btn${cur ? " set" : ""}${picked ? " pop" : ""}`}
        style={l ? ({ "--lc": `rgb(${l.rgb})` } as CSSProperties) : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        title="负责的同事"
        onClick={(e) => {
          e.stopPropagation();
          if (!open && box.current) {
            // Near the bottom of the screen the menu opens upward (prototype ChatOwnerPick).
            const r = box.current.getBoundingClientRect();
            setUp(window.innerHeight - r.bottom < 190 && r.top > 200);
          }
          setOpen((o) => !o);
        }}
      >
        {cur || "选负责"}
        <ShellIcon name="down" size={12} />
      </button>
      {open && (
        <span className={`own-menu${up ? " up" : ""}`} role="menu" onClick={(e) => e.stopPropagation()}>
          {[...names, ""].map((n) => (
            <button key={n || "none"} type="button" role="menuitemradio" aria-checked={n === cur} className={n === cur ? "on" : ""} onClick={() => void pick(n)}>
              <span className={`own-o${n ? "" : " nobody"}`}>
                {n && <i className="wl-dot" style={{ "--lc": `rgb(${ownerLabel(n).rgb})` } as CSSProperties} aria-hidden="true" />}
                {n || "不选"}
              </span>
              {n === cur && <ShellIcon name="check" size={14} />}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
